/**
 * No `id` or `data-testid` on the page twice while a chart is open in the lightbox
 * (launch round follow-up, 7 Oct 2026).
 *
 * The lightbox draws the same chart and legend again, so anything they carry with a fixed
 * `id` or `data-testid` would otherwise exist twice: once on the page under the dialog,
 * once inside it. Every such attribute inside the dialog that is also used outside it takes
 * a `-lightbox` suffix, and the references to a renamed `id` inside the dialog
 * (`url(#…)` paints, `href`, `aria-labelledby` and friends) follow it. Ids that are already
 * unique — `useId` ones, which differ between the two copies — are left alone.
 */

export const LIGHTBOX_SUFFIX = '-lightbox';

const REF_ATTRS = ['fill', 'stroke', 'clip-path', 'mask', 'filter', 'marker-start', 'marker-mid', 'marker-end'];
const LIST_ATTRS = ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'];

function escapeId(id: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id.replace(/["\\]/g, '\\$&');
}

/** Rename duplicated ids and test ids under `root`; `dialog` is the whole dialog, excluded from "outside". */
export function dedupeLightboxIds(root: Element | null, dialog: Element | null): void {
  if (!root || !dialog) return;
  const doc = root.ownerDocument;
  const usedOutside = (selector: string) =>
    [...doc.querySelectorAll(selector)].some((el) => !dialog.contains(el));

  for (const el of root.querySelectorAll<HTMLElement | SVGElement>('[data-testid]')) {
    const value = el.getAttribute('data-testid')!;
    if (value.endsWith(LIGHTBOX_SUFFIX)) continue;
    if (usedOutside(`[data-testid="${value.replace(/["\\]/g, '\\$&')}"]`)) {
      el.setAttribute('data-testid', `${value}${LIGHTBOX_SUFFIX}`);
    }
  }

  const renamed = new Map<string, string>();
  for (const el of root.querySelectorAll('[id]')) {
    const id = el.id;
    if (!id || id.endsWith(LIGHTBOX_SUFFIX)) continue;
    if (usedOutside(`#${escapeId(id)}`)) {
      const next = `${id}${LIGHTBOX_SUFFIX}`;
      el.id = next;
      renamed.set(id, next);
    }
  }
  if (renamed.size === 0) return;
  for (const el of root.querySelectorAll('*')) {
    for (const attr of REF_ATTRS) {
      const v = el.getAttribute(attr);
      const m = v ? /^url\(#(.+)\)$/.exec(v) : null;
      if (m && renamed.has(m[1]!)) el.setAttribute(attr, `url(#${renamed.get(m[1]!)})`);
    }
    for (const attr of ['href', 'xlink:href']) {
      const v = el.getAttribute(attr);
      if (v?.startsWith('#') && renamed.has(v.slice(1))) el.setAttribute(attr, `#${renamed.get(v.slice(1))}`);
    }
    for (const attr of LIST_ATTRS) {
      const v = el.getAttribute(attr);
      if (!v) continue;
      const next = v
        .split(/\s+/)
        .map((id) => renamed.get(id) ?? id)
        .join(' ');
      if (next !== v) el.setAttribute(attr, next);
    }
  }
}
